/**
 * External Imports
 */
import { FC, useCallback, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { format, subDays } from 'date-fns';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { RootState } from '../../../redux/store';
import { openModal, setModalType } from '../../../redux/actions';
import { logReportsClick, logTaskClick } from '../../../utils/analyticsHelpers';

/**
 * Style Imports
 */
import { StyledFilterBtn } from './FilterBtn.styles';

/**
 * Global Type Definition Imports
 */
import { Filter } from '../../../config/interfaces';
import ClearFilterBtn from '../clearFilterBtn';

interface FilterBtnProps {
  adminUserFilter: Filter | {};
  feedbackFilter: Filter | {};
  empFrequencyReportFilter: Filter | {};
  issueFilter: Filter | {};
  roundsReportFilter: Filter | {};
  censusReportFilter: Filter | {};
  stoplightFilter: Filter | {};
  complianceReportFilter: Filter | {};
  patientSummaryReportFilter: Filter | {};
  employeeSummaryReportFilter: Filter | {};
  employeeShiftStatusFilter: Filter | {};
}
const FilterBtn: FC<FilterBtnProps> = ({
  adminUserFilter,
  feedbackFilter,
  empFrequencyReportFilter,
  issueFilter,
  censusReportFilter,
  roundsReportFilter,
  stoplightFilter,
  complianceReportFilter,
  patientSummaryReportFilter,
  employeeSummaryReportFilter,
  employeeShiftStatusFilter
}) => {
  const dispatch = useDispatch();

  const { logTrackingEvent } = useAnalyticsApi();
  const { '*': view } = useParams();
  const chosenFilter: Filter | {} = useMemo(() => {
    switch (view) {
      case 'issues':
      case 'cned/issues':
      case 'csc/issues':
        return issueFilter;
      case 'feedback':
      case 'cned/feedback':
      case 'csc/feedback':
        return feedbackFilter;
      case 'stoplight':
        return stoplightFilter;
      case 'patient':
      case 'patient/compliance':
        return complianceReportFilter;
      case 'patient/summary':
        return patientSummaryReportFilter;
      case 'patient/census':
        return censusReportFilter;
      case 'patient/rounds':
      case 'employee':
      case 'employee/rounds':
        return roundsReportFilter;
      case 'employee/frequency':
        return empFrequencyReportFilter;
      case 'employee/summary':
        return employeeSummaryReportFilter;
      case 'users':
        return adminUserFilter;
      default:
        return {};
    }
  }, [
    adminUserFilter,
    censusReportFilter,
    complianceReportFilter,
    empFrequencyReportFilter,
    feedbackFilter,
    issueFilter,
    roundsReportFilter,
    stoplightFilter,
    patientSummaryReportFilter,
    employeeSummaryReportFilter,
    view
  ]);

  const filterCount = useMemo(() => {
    const cleanFilter = { ...chosenFilter } as Filter;
    const today = new Date();

    const isTaskDateRangeFilterApplied =
      cleanFilter.startDate &&
      cleanFilter.endDate &&
      !(
        cleanFilter.startDate === format(subDays(today, 30), 'yyyy-MM-dd') &&
        cleanFilter.endDate === format(today, 'yyyy-MM-dd')
      );

    const isSummaryDateRangeDefault =
      cleanFilter.startDate &&
      cleanFilter.endDate &&
      cleanFilter.startDate === format(new Date(), 'yyyy-MM-dd') &&
      cleanFilter.endDate === format(new Date(), 'yyyy-MM-dd');

    const isRoundsReportDateRangeDefault =
      cleanFilter.dateRangeText && cleanFilter.dateRangeText === 'Today';

    if (view?.includes('csc/list')) {
      return employeeShiftStatusFilter !== '' ? 1 : 0;
    }

    switch (view) {
      case 'issues':
      case 'cned/issues':
      case 'feedback':
      case 'cned/feedback':
      case 'csc/issues':
      case 'csc/feedback':
      case 'stoplight':
        if (!isTaskDateRangeFilterApplied) {
          delete cleanFilter.startDate;
          delete cleanFilter.endDate;
        }
        return (
          Object.values(cleanFilter).filter((val: any) => val).length -
          (isTaskDateRangeFilterApplied ? 1 : 0)
        );
      case 'patient/census':
      case 'patient/rounds':
      case 'patient/compliance':
      case 'employee':
      case 'employee/frequency':
      case 'employee/rounds':
        if (Object.values(cleanFilter).filter((val: any) => val).length > 0) {
          delete cleanFilter.startDate;
          delete cleanFilter.endDate;
        }
        return (
          Object.values(cleanFilter).filter((val: any) => val).length -
          (isRoundsReportDateRangeDefault ? 1 : 0)
        );
      case 'patient/summary':
        if (!isSummaryDateRangeDefault) {
          delete cleanFilter.startDate;
          delete cleanFilter.endDate;
        }
        return (
          Object.values(cleanFilter).filter((val: any) => val).length -
          (isRoundsReportDateRangeDefault ? 1 : 0)
        );
      case 'users':
        if (Object.values(cleanFilter).filter((val: any) => val).length > 0) {
          delete cleanFilter.startDate;
          delete cleanFilter.endDate;
        }
        return (
          Object.values(cleanFilter).filter((val: any) => val).length -
          (cleanFilter.accessLevel && cleanFilter.accessLevel === 'master'
            ? 1
            : 0)
        );
      default:
        return Object.values(cleanFilter).filter((val: any) => val).length;
    }
  }, [chosenFilter, view, employeeShiftStatusFilter]);

  const filterIssues = () => {
    dispatch(setModalType('Filter Issues'));
    dispatch(openModal());
  };

  const filterFeedback = () => {
    dispatch(setModalType('Filter Feedback'));
    dispatch(openModal());
  };

  const filterStoplights = () => {
    dispatch(setModalType('Filter Stoplights'));
    dispatch(openModal());
  };

  const filterAdminUsers = () => {
    dispatch(setModalType('Filter Admin Users'));
    dispatch(openModal());
  };

  const filterRoundsReport = () => {
    dispatch(setModalType('Filter Rounds Report'));
    dispatch(openModal());
  };

  const filterComplianceReport = () => {
    dispatch(setModalType('Filter Compliance Report'));
    dispatch(openModal());
  };

  const filterSummaryReport = () => {
    dispatch(setModalType('Filter Summary Report'));
    dispatch(openModal());
  };

  const filterEmployeeSummaryReport = () => {
    dispatch(setModalType('Filter Employee Summary Report'));
    dispatch(openModal());
  };
  const filterCensusReport = () => {
    dispatch(setModalType('Filter Census Report'));
    dispatch(openModal());
  };
  const filterFrequencyReport = () => {
    dispatch(setModalType('Filter Frequency Report'));
    dispatch(openModal());
  };

  // this function is generalized for future use in other lists. It's currently only being used in CSC list
  const filterSubjectsToRoundList = () => {
    dispatch(setModalType('Filter Subjects To Round List'));
    dispatch(openModal());
  };

  const handleFilterClick = useCallback(() => {
    switch (view) {
      case 'issues':
      case 'cned/issues':
      case 'feedback':
      case 'cned/feedback':
      case 'csc/issues':
      case 'csc/feedback':
      case 'stoplight':
        logTrackingEvent(logTaskClick(view, 'filter'));
        break;
      case 'employee':
        logTrackingEvent(logReportsClick('employee-rounds', 'filter'));
        break;
      case 'employee/rounds':
      case 'employee/summary':
      case 'employee/frequency':
      case 'employee/compliance':
      case 'patient/compliance':
      case 'patient/summary':
      case 'patient/rounds':
      case 'patient/census':
        logTrackingEvent(logReportsClick(view?.replace('/', '-'), 'filter'));
        break;
      default:
        console.log('No view for event');
    }
    return view?.includes('issues')
      ? filterIssues()
      : view?.includes('feedback')
      ? filterFeedback()
      : view?.includes('stoplight')
      ? filterStoplights()
      : view === 'users'
      ? filterAdminUsers()
      : view === 'patient/census'
      ? filterCensusReport()
      : view?.includes('compliance') || view === 'patient'
      ? filterComplianceReport()
      : view?.includes('patient/summary')
      ? filterSummaryReport()
      : view === 'employee/summary'
      ? filterEmployeeSummaryReport()
      : view === 'employee/frequency'
      ? filterFrequencyReport()
      : view?.includes('csc/list')
      ? filterSubjectsToRoundList()
      : filterRoundsReport();
  }, [view, logTrackingEvent, logReportsClick, logTaskClick]);

  return (
    <div className="w-100">
      <StyledFilterBtn
        id="Filter-Button"
        className="me-1"
        color="orange"
        icon="filter_list"
        kind={filterCount > 0 ? 'primary' : 'secondary'}
        size="small"
        onClick={handleFilterClick}
      >
        {/* comments for adding numbers back in the future */}
        {/* {!isFilterOn && ( */}
        {/* )} */}
        Filter
        {/* {howManyFilters} Filter{howManyFilters && howManyFilters > 1 ? 's' : ''} */}
      </StyledFilterBtn>
      {filterCount > 0 && <ClearFilterBtn />}
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    modalState: state.ModalReducer.modal,
    issueFilter: state.TaskReducer.issueFilter,
    feedbackFilter: state.TaskReducer.feedbackFilter,
    stoplightFilter: state.StoplightReducer.stoplightFilter,
    roundsReportFilter: state.ReportReducer.roundsReportFilter,
    censusReportFilter: state.ReportReducer.censusReportFilter,
    empFrequencyReportFilter: state.ReportReducer.frequencyReportFilter,
    adminUserFilter: state.AdminReducer.adminUserFilter,
    complianceReportFilter: state.ReportReducer.complianceReportFilter,
    patientSummaryReportFilter: state.ReportReducer.patientSummaryReportFilter,
    employeeSummaryReportFilter:
      state.ReportReducer.employeeSummaryReportFilter,
    employeeShiftStatusFilter: state.EmployeeReducer.shiftStatusFilter
  };
};

export default connect(mapReduxStateToProps)(FilterBtn);
